/**
 * OpenStack API client.
 */
import apiClient from './client';

export interface OpenStackCloud {
  id: number;
  user_id: number;
  cloud_name: string;
  project_name?: string;
  auth_type: 'application_credential' | 'password';
  auth_url: string;
  region_name?: string;
  interface?: string;
  identity_api_version?: string;
  openstack_user_id?: string;
  openstack_user_name?: string;  // Computed from openstack_user_mappings relationship
  matched_app_user_id?: number;
  status: 'uploaded' | 'refreshed' | 'error';
  status_message?: string;
  last_refreshed_at?: string;
  created_at: string;
  updated_at?: string;
  is_duplicate?: boolean;  // True if this cloud already existed when uploaded
  owner_name?: string;  // Name of the cloud owner
  owner_email?: string;  // Email of the cloud owner
}

export interface OpenStackCloudCreate {
  cloud_name: string;
  project_name?: string;
  auth_type: 'application_credential' | 'password';
  auth_url: string;
  region_name?: string;
  interface?: string;
  identity_api_version?: string;
  application_credential_id?: string;
  application_credential_secret?: string;
  username?: string;
  password?: string;
  project_id?: string;
}

export interface OpenStackCloudUpdate {
  cloud_name?: string;
  project_name?: string;
}

export interface OpenStackProjectQuota {
  instances_allocated: number;
  instances_quota?: number;
  cores_allocated: number;
  cores_quota?: number;
  memory_allocated: number;
  memory_quota?: number;
  volumes_allocated: number;
  volumes_quota?: number;
  volume_snapshots_allocated: number;
  volume_snapshots_quota?: number;
  volume_storage_allocated: number;
  volume_storage_quota?: number;
  floating_ips_allocated: number;
  floating_ips_quota?: number;
  security_groups_allocated: number;
  security_groups_quota?: number;
  security_group_rules_allocated: number;
  security_group_rules_quota?: number;
  networks_allocated: number;
  networks_quota?: number;
  ports_allocated: number;
  ports_quota?: number;
  routers_allocated: number;
  routers_quota?: number;
}

export interface OpenStackProject {
  id: number;
  cloud_id: number;
  project_id: string;
  project_name?: string;  // Official project name from OpenStack API
  last_refreshed: string;
  quotas: OpenStackProjectQuota;
}

export interface CloudTestResult {
  success: boolean;
}

export interface VMIPAddress {
  network: string;
  address: string;
  version: number;
  type: 'fixed' | 'floating';
}

export interface VMPort {
  id: string;
  name?: string;
  mac_address?: string;
  network_id?: string;
  fixed_ips: Array<{ ip_address: string; subnet_id?: string }>;
  security_groups: string[];
  status?: string;
}

export interface VMDisk {
  id?: string;
  name?: string;
  size_gb?: number;
  type?: string;
  device?: string;
  boot_index?: number;
}

export interface VMSecurityGroup {
  id?: string;
  name?: string;
  description?: string;
}

export interface VMSecurityGroupRule {
  id?: string;
  direction?: string; // ingress/egress
  ethertype?: string; // IPv4/IPv6
  protocol?: string; // tcp, udp, icmp, null
  port_range_min?: number;
  port_range_max?: number;
  remote_ip_prefix?: string; // CIDR
  remote_group_id?: string;
  remote_group_name?: string;
  security_group_id?: string;
  security_group_name?: string;
  description?: string;
}

export interface OpenStackVM {
  id: number;
  project_id: string;
  vm_id: string;
  vm_name?: string;
  status?: string;
  flavor_id?: string;
  flavor_name?: string;
  cores?: number;
  ram_mb?: number;
  disk_gb?: number;
  created_at?: string;
  created_by_user_id?: string;
  created_by_user_name?: string;
  image_id?: string;
  image_name?: string;
  key_name?: string;
  last_refreshed: string;
}

export interface OpenStackVMListResponse {
  vms: OpenStackVM[];
  project_id: string;
  total: number;
}

export interface KubernetesCluster {
  cluster_name: string;
  control_plane_count: number;
  worker_count: number;
  total_vms: number;
  vms: OpenStackVM[];
}

export interface KubernetesClusterListResponse {
  clusters: KubernetesCluster[];
  project_id: string;
  total: number;
}

export interface OpenStackVMDetail extends OpenStackVM {
  ip_addresses: VMIPAddress[];
  ports: VMPort[];
  disks: VMDisk[];
  security_groups: VMSecurityGroup[];
  security_group_rules: VMSecurityGroupRule[];
  availability_zone?: string;
  host?: string;
  hypervisor_hostname?: string;
  power_state?: string;
  task_state?: string;
  vm_state?: string;
  metadata: Record<string, any>;
}

export interface RefreshResult {
  refreshed: number;
  errors: number;
}

export interface ProjectRefreshResult {
  success: boolean;
  project_id: string;
  updated_caches: number;
}

export interface CloudYamlUploadResponse {
  clouds: OpenStackCloud[];
  added_count: number;
  duplicate_count: number;
  rejected_count: number;
  rejected_clouds: Array<{
    cloud_name: string;
    reason: string;
  }>;
  message?: string;
}

/**
 * Upload a cloud.yaml file.
 */
export async function uploadCloudYaml(file: File): Promise<CloudYamlUploadResponse> {
  const formData = new FormData();
  formData.append('file', file);
  
  const response = await apiClient.post<CloudYamlUploadResponse>('/api/openstack/clouds/upload-yaml', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  
  return response.data;
}

/**
 * Create a new cloud configuration.
 */
export async function createCloud(cloudData: OpenStackCloudCreate): Promise<OpenStackCloud> {
  const response = await apiClient.post<OpenStackCloud>('/api/openstack/clouds', cloudData);
  return response.data;
}

/**
 * List all cloud configurations for the current user.
 * @param skip Number of records to skip (for pagination)
 * @param limit Maximum number of records to return (for pagination, max 1000)
 */
export async function listClouds(skip: number = 0, limit: number = 1000): Promise<OpenStackCloud[]> {
  const response = await apiClient.get<OpenStackCloud[]>('/api/openstack/clouds', {
    params: { skip, limit },
  });
  return response.data;
}

/**
 * Get a specific cloud configuration.
 */
export async function getCloud(cloudId: number): Promise<OpenStackCloud> {
  const response = await apiClient.get<OpenStackCloud>(`/api/openstack/clouds/${cloudId}`);
  return response.data;
}

/**
 * Update a cloud configuration (friendly names only).
 */
export async function updateCloud(
  cloudId: number,
  updates: OpenStackCloudUpdate
): Promise<OpenStackCloud> {
  const response = await apiClient.put<OpenStackCloud>(`/api/openstack/clouds/${cloudId}`, updates);
  return response.data;
}

/**
 * Delete a cloud configuration.
 */
export async function deleteCloud(cloudId: number): Promise<void> {
  await apiClient.delete(`/api/openstack/clouds/${cloudId}`);
}

/**
 * Test OpenStack connection for a cloud configuration.
 */
export async function testCloudConnection(cloudId: number): Promise<CloudTestResult> {
  const response = await apiClient.post<CloudTestResult>(`/api/openstack/clouds/${cloudId}/test`);
  return response.data;
}

/**
 * List all projects for the current user.
 * @param skip Number of records to skip (for pagination)
 * @param limit Maximum number of records to return (for pagination, max 1000)
 */
export async function listProjects(skip: number = 0, limit: number = 1000): Promise<OpenStackProject[]> {
  const response = await apiClient.get<OpenStackProject[]>('/api/openstack/projects', {
    params: { skip, limit },
  });
  return response.data;
}

/**
 * Get details for a specific project.
 */
export async function getProject(projectId: string): Promise<OpenStackProject> {
  const response = await apiClient.get<OpenStackProject>(`/api/openstack/projects/${projectId}`);
  return response.data;
}

/**
 * Refresh all projects.
 */
export async function refreshProjects(): Promise<{ message: string; user_id: number }> {
  const response = await apiClient.post<{ message: string; user_id: number }>('/api/openstack/projects/refresh');
  return response.data;
}

/**
 * Refresh a specific project.
 */
export async function refreshProject(projectId: string): Promise<ProjectRefreshResult> {
  const response = await apiClient.post<ProjectRefreshResult>(
    `/api/openstack/projects/${projectId}/refresh`
  );
  return response.data;
}

/**
 * Refresh projects for a single cloud.
 */
export async function refreshCloudProjects(cloudId: number): Promise<RefreshResult> {
  const response = await apiClient.post<RefreshResult>(
    `/api/openstack/clouds/${cloudId}/refresh-projects`
  );
  return response.data;
}

export interface OpenStackStatistics {
  total_clouds: number;
  total_projects: number;
  total_openstack_users: number;
  total_vms_allocated: number;
  total_vms_quota: number | null;
  total_cores_allocated: number;
  total_cores_quota: number | null;
  total_ram_gb_allocated: number;
  total_ram_gb_quota: number | null;
  total_disk_gb_allocated: number;
  total_disk_gb_quota: number | null;
}

/**
 * Get aggregated OpenStack statistics (admin only).
 */
export async function getOpenStackStatistics(): Promise<OpenStackStatistics> {
  const response = await apiClient.get<OpenStackStatistics>('/api/openstack/admin/statistics');
  return response.data;
}

/**
 * List all VMs for a project.
 */
export async function listProjectVMs(projectId: string): Promise<OpenStackVMListResponse> {
  const response = await apiClient.get<OpenStackVMListResponse>(
    `/api/openstack/projects/${projectId}/vms`
  );
  return response.data;
}

/**
 * Refresh VM cache for a project.
 */
export async function refreshProjectVMs(projectId: string): Promise<{ success: boolean; project_id: string; vm_count: number }> {
  const response = await apiClient.post<{ success: boolean; project_id: string; vm_count: number }>(
    `/api/openstack/projects/${projectId}/vms/refresh`
  );
  return response.data;
}

/**
 * Get detailed VM information.
 */
export async function getVMDetails(vmId: string): Promise<OpenStackVMDetail> {
  const response = await apiClient.get<OpenStackVMDetail>(`/api/openstack/vms/${vmId}`);
  return response.data;
}

/**
 * Refresh VM detail cache.
 */
export async function refreshVMDetails(vmId: string): Promise<{ success: boolean; vm_id: string }> {
  const response = await apiClient.post<{ success: boolean; vm_id: string }>(
    `/api/openstack/vms/${vmId}/refresh`
  );
  return response.data;
}

/**
 * Cloud sharing interfaces and functions.
 */
export interface CloudShareUser {
  user_id: number;
  email: string;
  name: string;
  shared_at: string;
}

export interface CloudShareList {
  cloud_id: number;
  is_owner: boolean;
  shared_users: CloudShareUser[];
}

export interface CloudShareAddRequest {
  user_id: number;
}

/**
 * List users who have been shared access to a cloud.
 */
export async function listCloudShares(cloudId: number): Promise<CloudShareList> {
  const response = await apiClient.get<CloudShareList>(`/api/openstack/clouds/${cloudId}/shares`);
  return response.data;
}

/**
 * Share a cloud with a user.
 */
export async function addCloudShare(cloudId: number, userId: number): Promise<CloudShareUser> {
  const { getCsrfHeader } = await import('./csrf');
  const csrfHeaders = await getCsrfHeader();
  
  const response = await apiClient.post<CloudShareUser>(
    `/api/openstack/clouds/${cloudId}/shares`,
    { user_id: userId } as CloudShareAddRequest,
    { headers: csrfHeaders }
  );
  return response.data;
}

/**
 * Remove shared access for a user.
 */
export async function removeCloudShare(cloudId: number, userId: number): Promise<void> {
  const { getCsrfHeader } = await import('./csrf');
  const csrfHeaders = await getCsrfHeader();
  
  await apiClient.delete(`/api/openstack/clouds/${cloudId}/shares/${userId}`, {
    headers: csrfHeaders,
  });
}

/**
 * Search result item interface.
 */
export interface SearchResultItem {
  type: 'project' | 'vm';
  id: string;
  name: string | null;
  project_id: string;
  project_name?: string | null;
  cloud_name?: string | null;
  match_field?: string | null;
  match_value?: string | null;
  cloud_id?: number | null;
}

/**
 * Search response interface.
 */
export interface SearchResponse {
  query: string;
  results: SearchResultItem[];
  total: number;
}

/**
 * Search cached OpenStack data (projects, VMs, IP addresses, keys).
 * 
 * @param query Search query string
 * @param limit Maximum number of results (default 50, max 100)
 * @returns Search response with matching projects and VMs
 */
export async function search(query: string, limit: number = 50): Promise<SearchResponse> {
  const response = await apiClient.get<SearchResponse>('/api/openstack/search', {
    params: { q: query, limit },
  });
  return response.data;
}

/**
 * List all Kubernetes clusters for a project.
 * 
 * @param projectId OpenStack project ID
 * @returns List of detected Kubernetes clusters
 */
export async function listProjectClusters(projectId: string): Promise<KubernetesClusterListResponse> {
  const response = await apiClient.get<KubernetesClusterListResponse>(
    `/api/openstack/projects/${projectId}/clusters`
  );
  return response.data;
}

/**
 * Get details for a specific Kubernetes cluster.
 * 
 * @param projectId OpenStack project ID
 * @param clusterName Cluster name
 * @returns Cluster details with all VMs
 */
export async function getCluster(projectId: string, clusterName: string): Promise<KubernetesCluster> {
  const response = await apiClient.get<KubernetesCluster>(
    `/api/openstack/projects/${projectId}/clusters/${encodeURIComponent(clusterName)}`
  );
  return response.data;
}
